import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Gap left between the POV decision group's bottom edge and the bet tray's top
 * rope. One number, every device — the whole point of measuring instead of
 * predicting. Comfortably more than the 4px gap INSIDE the group, so cards and
 * hand strength read as one unit set against the tray.
 */
export const TRAY_CLEARANCE_GAP_PX = 12;

/** Debounce for re-measuring after a viewport change (matches Table/Bet). */
const RESIZE_SETTLE_MS = 150;

/**
 * The cumulative visual scale an element is painted at. The seat renders inside
 * a stack of scales (--seat-scale, the player's Table View Size, grid-cell
 * sizing), so reading any single CSS variable gets this wrong — one measured
 * 0.688 where --seat-scale alone claimed 0.8125, which silently ate two thirds
 * of the clearance. Painted height over layout height is the whole stack,
 * whatever produced it.
 */
export const visualScaleOf = (el: HTMLElement): number =>
  el.offsetHeight > 0 ? el.getBoundingClientRect().height / el.offsetHeight : 1;

/**
 * The lowest painted edge anywhere inside `group`, not just the group's own
 * border box. Absolutely-positioned descendants do not enlarge their parent's
 * box, so the landscape seat's card column — which carries the hand-strength
 * label BELOW the avatar pill — was invisible to a plain rect read and hung
 * ~4px under the tray after the lift. "Nothing in the group is occluded" has
 * to mean every child, so we take the deepest of them.
 */
const deepestBottom = (group: Element): number => {
  let bottom = group.getBoundingClientRect().bottom;
  group.querySelectorAll('*').forEach((child) => {
    const r = child.getBoundingClientRect();
    // Skip unrendered nodes; a zero-size rect reports at the origin and would
    // otherwise drag the answer around.
    if (r.height > 0 && r.width > 0 && r.bottom > bottom) bottom = r.bottom;
  });
  return bottom;
};

/**
 * How far (in VISUAL px) `group` has to rise to clear `tray` by the shared gap.
 * 0 when it already clears. Shared so every piece that must travel together —
 * the POV seat and its bet chip — moves by the identical amount instead of each
 * deriving its own answer.
 */
export const measureTrayClearanceVisualPx = (
  group: Element,
  tray: Element,
  currentLiftVisualPx = 0
): number => {
  const restBottom = deepestBottom(group) + currentLiftVisualPx;
  const overlap = restBottom - tray.getBoundingClientRect().top;
  return overlap <= 0 ? 0 : overlap + TRAY_CLEARANCE_GAP_PX;
};

/**
 * Sampling schedule (ms after open) for letting the tray's top edge settle
 * before committing to a target. Covers the footer's ~0.4s slide-up entrance
 * and never hangs: whatever the reading is at the last sample, we take it.
 *
 * Plain timeouts, not rAF — the same choice RaiseActionExpanded's ring-scale
 * lookup makes, and for the same reason: a backgrounded or throttled tab
 * freezes animation frames entirely but still runs (clamped) timers. With rAF
 * the lift would simply never apply in a background tab, leaving the cards
 * covered when the player came back.
 */
export const SETTLE_SAMPLE_MS = [0, 50, 120, 220, 350, 500];

/**
 * The visual travel currently applied to an element by `useTrayClearanceLift`
 * — read straight off its transform. Lets a sibling that must move in lockstep
 * (the POV seat's bet chip) copy the exact number rather than re-deriving one.
 */
export const appliedLiftVisualPx = (el: Element): number => {
  const transform = getComputedStyle(el).transform;
  if (!transform || transform === 'none') return 0;
  const ty = new DOMMatrixReadOnly(transform).m42;
  return Math.max(0, -ty * visualScaleOf(el as HTMLElement));
};

/**
 * EN-10420 — lift a POV element group clear of the open bet/raise tray.
 *
 * WHY MEASURED, NOT PREDICTED. Earlier passes derived the travel from viewport
 * height. No such formula can be right: the overlap depends on the tray's own
 * height (which changes with width as its controls reflow) AND on where the
 * seat lands (which the table's responsive scale decides from its measured
 * box). Fitting a line to a handful of phones left laptops and half-snapped
 * windows still covered while phones floated with dead space under them —
 * exactly the "extra empty space below the POV cards" QA reported (Jira 66026).
 * Measuring the real overlap makes the gap identical on every device by
 * construction, with no per-device error to chase.
 *
 * WHY THIS IS SAFE FOR MOTION. The EN-10420 rule is that a target must never
 * move mid-glide — the earlier stutters came from writing a SECOND target while
 * the first was still animating. This measures exactly once per tray-open, in a
 * layout effect, with the group at rest and before the browser paints; the
 * transition then runs to a target that cannot change under it. The only
 * re-measure is on an actual viewport change (debounced), which is precisely
 * when the target SHOULD move — and it un-applies the current lift first, so it
 * always measures the resting geometry rather than compounding.
 *
 * Returns a ready-to-use CSS transform (`none` when nothing is occluded, which
 * is the correct answer on tall or wide layouts where the tray never reaches
 * the group).
 */
export function useTrayClearanceLift(
  isTrayOpen: boolean,
  groupRef: RefObject<HTMLElement>
): string {
  const [liftPx, setLiftPx] = useState(0);
  // The applied lift, readable synchronously by a re-measure so it can undo
  // itself and read the group's RESTING position.
  const liftPxRef = useRef(0);

  const apply = useCallback((px: number) => {
    liftPxRef.current = px;
    setLiftPx(px);
  }, []);

  /** One reading of the geometry, or null when the pieces are not both up. */
  const read = useCallback(() => {
    const group = groupRef.current;
    if (!group) return null;
    // Scope to this table's own view (single-table AND each grid cell wrap in
    // `[data-table-view-root]`), so a grid cell can never measure against a
    // sibling table's tray. Stories without the wrapper fall back to the
    // document, where only one table exists anyway.
    const scope = group.closest('[data-table-view-root]') ?? document;
    const tray = scope.querySelector('[data-bet-tray]');
    if (!tray) return null;

    const scale = visualScaleOf(group);
    const neededVisual = measureTrayClearanceVisualPx(
      group,
      tray,
      // Undo the lift currently applied (stored in element space) so we always
      // read the RESTING geometry rather than compounding.
      liftPxRef.current * scale
    );
    return {
      trayTop: tray.getBoundingClientRect().top,
      // Element space: the transform rides inside the scaled seat.
      lift: neededVisual === 0 ? 0 : Math.round(neededVisual / (scale || 1))
    };
  }, [groupRef]);

  const measure = useCallback(() => {
    const reading = read();
    if (reading) apply(reading.lift);
  }, [read, apply]);

  useLayoutEffect(() => {
    if (!isTrayOpen) {
      apply(0);
      return undefined;
    }
    // The tray's top edge is not trustworthy on the first frame: the footer's
    // action row plays a slide-up entrance (gsap, PokerActions), so a tray
    // measured mid-slide reads as much lower than it lands — which under-lifts
    // the group and eats the gap. Wait for the edge to hold still for two
    // consecutive frames, THEN choose the target and start the glide. The
    // group has not moved yet, so nothing is being retargeted mid-flight; the
    // cards simply begin a frame or two later, which is imperceptible.
    let timer = 0;
    let sample = 0;
    let lastTrayTop: number | null = null;
    const settle = () => {
      const reading = read();
      const isLastSample = sample >= SETTLE_SAMPLE_MS.length - 1;
      if (reading) {
        const isStable = lastTrayTop !== null && Math.abs(reading.trayTop - lastTrayTop) < 0.5;
        if (isStable || isLastSample) {
          apply(reading.lift);
          return;
        }
        lastTrayTop = reading.trayTop;
      }
      if (isLastSample) return;
      sample += 1;
      timer = window.setTimeout(settle, SETTLE_SAMPLE_MS[sample]);
    };
    timer = window.setTimeout(settle, SETTLE_SAMPLE_MS[0]);
    return () => window.clearTimeout(timer);
  }, [isTrayOpen, read, apply]);

  useEffect(() => {
    if (!isTrayOpen) return undefined;
    let timer = 0;
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(measure, RESIZE_SETTLE_MS);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, [isTrayOpen, measure]);

  return liftPx === 0 ? 'none' : `translateY(-${liftPx}px)`;
}
